/**
 * useCoverAsset – get the first cover MediaAsset for an entity.
 * Returns { asset, url320, url640 } with automatic fallback.
 */
import { useMediaAssets } from './useMediaAssets';
import { resolveAssetUrl } from '@/lib/media/mediaApi';
import type { MediaAsset, OwnerType } from '@/lib/media/mediaTypes';

export interface CoverAssetResult {
  asset: MediaAsset | null;
  url320: string | null;
  url640: string | null;
  url1280: string | null;
  isLoading: boolean;
}

export function useCoverAsset(
  ownerType: OwnerType,
  ownerId: string | null | undefined,
): CoverAssetResult {
  const { data, isLoading } = useMediaAssets(ownerType, ownerId, 'cover', null);
  // Pick the newest cover asset (by created_at) to stay consistent with
  // MediaImageManager.activeCover and useCoverImageUrl.
  const asset = (() => {
    if (!data || data.length === 0) return null;
    if (data.length === 1) return data[0];
    return [...data].sort((a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )[0];
  })();
  return {
    asset,
    url320: asset ? resolveAssetUrl(asset, 320) : null,
    url640: asset ? resolveAssetUrl(asset, 640) : null,
    url1280: asset ? resolveAssetUrl(asset, 1280) : null,
    isLoading,
  };
}
